import { useLocalization } from '@lib/localization';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { type SsoConfig, type SsoTestResult } from '@store/api/ssoEndpoints';
import { type FC, type ReactNode } from 'react';
import { ssoProviderPresets } from './ssoPresets';

interface Props {
  result: SsoTestResult;
  /** Entra vouches for an address in its own way, and says why it did not. */
  provider: SsoConfig['provider'];
}

const Row: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ minWidth: 0, flex: 1, fontSize: 13.5, overflowWrap: 'anywhere' }}>{children}</Box>
  </Stack>
);

/**
 * What a trial sign-in found, for the sysadmin who ran it: who the provider said it was, in
 * the claims the settings read, and what signing in as that person would have done. Nothing
 * was done: no account was made or changed, and nobody was signed in.
 *
 * A trial the provider answered passes, whatever account would follow: the connection works,
 * and what this person would get is the accounts' settings to decide. Refused for the account,
 * it is a warning; refused before the provider said who it was, an error.
 */
const SsoTrialResult: FC<Props> = ({ result, provider }) => {
  const { unverifiedEmailNote } = ssoProviderPresets[provider];
  const { t } = useLocalization();
  const { identity, account } = result;

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Alert severity={result.ok ? 'success' : identity ? 'warning' : 'error'}>
        {result.ok
          ? t(`ServerConfig_Sso_Trial_${account?.action === 'created' ? 'WouldCreate' : account?.action === 'linked' ? 'WouldLink' : 'WouldSignIn'}`)
          : (
            <>
              {t(identity ? 'ServerConfig_Sso_Trial_RefusedAccount' : 'ServerConfig_Sso_Trial_Refused')}
              {/* The server's own words, and the provider's inside them: this is where a wrong
                  secret or a missing claim is read. */}
              <Typography component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: 12.5, overflowWrap: 'anywhere' }}>
                {[result.error, result.message].filter(Boolean).join(': ')}
              </Typography>
            </>
          )}
      </Alert>

      {identity && (
        <Stack spacing={0.75} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>{t('ServerConfig_Sso_Trial_Identity')}</Typography>
          <Row label={t('ServerConfig_Sso_Claim_Subject')}>{identity.subject}</Row>
          <Row label={t('ServerConfig_Sso_Claim_Email')}>
            {identity.email ?? '—'}
            {identity.email && !identity.emailVerified && (
              <Chip size="small" color="warning" label={t('ServerConfig_Sso_Trial_EmailUnverified')} sx={{ ml: 1, height: 20 }} />
            )}
            {unverifiedEmailNote && identity.email && !identity.emailVerified && (
              <Typography component="div" variant="caption" color="text.secondary">
                {t(unverifiedEmailNote)}
              </Typography>
            )}
          </Row>
          <Row label={t('ServerConfig_Sso_Claim_Name')}>{identity.name ?? '—'}</Row>
          <Row label={t('ServerConfig_Sso_Claim_Username')}>{identity.username ?? '—'}</Row>
          <Row label={identity.mappingClaim}>
            {identity.mappingValues.length === 0
              ? t(identity.groupsOverage
                ? 'ServerConfig_Sso_Trial_GroupsOverage'
                : identity.mappingUnknown ? 'ServerConfig_Sso_Trial_ValuesUnknown' : 'ServerConfig_Sso_Trial_NoValues')
              : (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                  {identity.mappingValues.map((value) => (
                    <Chip key={value} size="small" label={value} sx={{ height: 20, fontFamily: 'monospace', fontSize: 11.5 }} />
                  ))}
                </Stack>
              )}
          </Row>
        </Stack>
      )}

      {account && (
        <Stack spacing={0.75} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>{t('ServerConfig_Sso_Trial_Account')}</Typography>
          <Row label={t('Common_Username')}>{account.username}</Row>
          <Row label={t('Common_Name')}>{account.name}</Row>
          <Row label={t('Common_Role')}>{t(`User_Role_${account.role.capitalize()}`)}</Row>
          <Row label={t('Common_Teams')}>
            {account.teams.length === 0
              ? '—'
              : account.teams.map((team) => `${team.name} (${t(`Team_Role_${team.role.toLowerCase().capitalize()}`)})`).join(', ')}
          </Row>
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
            {t(account.placed ? 'ServerConfig_Sso_Trial_Placed' : 'ServerConfig_Sso_Trial_NotPlaced')}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default SsoTrialResult;
